import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Divider,
  TextField,
  Chip,
  Alert,
} from '@mui/material';
import {
  CheckCircleOutline as ApproveIcon,
  CancelOutlined as DenyIcon,
  ErrorOutline as ErrorIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { applyLanguage } from '../i18n';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000/api/v1';

const formatCurrency = (cents: number, quote?: QuoteData | null) => {
  return new Intl.NumberFormat(quote?.account?.settings?.locale || quote?.account?.locale || 'en-US', {
    style: 'currency',
    currency: quote?.account?.settings?.currencyCode || quote?.account?.currencyCode || 'USD',
  }).format(cents / 100);
};

const formatDate = (dateString: string | null | undefined, locale = 'en-US') => {
  if (!dateString) return '';
  return new Date(dateString).toLocaleDateString(locale, {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
};

interface QuoteData {
  id: string;
  quoteNumber: string;
  status: string;
  title?: string;
  subtotal: number;
  taxAmount: number;
  total: number;
  depositPercent: number;
  depositAmount: number;
  depositPaid: number;
  expiresAt?: string;
  notes?: string;
  footerNotes?: string;
  lineItems: {
    id: string;
    description: string;
    quantity: number;
    unitPrice: number;
    amount: number;
  }[];
  customer: {
    firstName?: string;
    lastName?: string;
    companyName?: string;
  };
  property?: {
    addressLine1: string;
    city: string;
    state: string;
  };
  account: {
    businessName: string;
    logoUrl?: string;
    primaryColor?: string;
    businessPhone?: string;
    businessEmail?: string;
    currencyCode?: string;
    locale?: string;
    customerLanguage?: string;
    settings?: {
      currencyCode?: string;
      locale?: string;
      customerLanguage?: string;
    };
  };
}

type PageState = 'loading' | 'ready' | 'approved' | 'denied' | 'error' | 'expired' | 'already_actioned';

export const QuoteApproval: React.FC = () => {
  const { t } = useTranslation();
  const { token } = useParams<{ token: string }>();
  const [quote, setQuote] = useState<QuoteData | null>(null);
  const [pageState, setPageState] = useState<PageState>('loading');
  const [errorMessage, setErrorMessage] = useState('');
  const [showDenyForm, setShowDenyForm] = useState(false);
  const [denialReason, setDenialReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    if (!token) {
      setPageState('error');
      setErrorMessage(t('No quote token provided.'));
      return;
    }

    fetch(`${API_BASE_URL}/quote-approval/${token}`)
      .then(async (res) => {
        if (!res.ok) {
          const err = await res.json().catch(() => ({ message: t('Failed to load quote') }));
          throw new Error(err.message);
        }
        return res.json();
      })
      .then((data) => {
        applyLanguage(
          data.account?.settings?.customerLanguage ||
          data.account?.settings?.locale ||
          data.account?.locale,
        );
        setQuote(data);
        if (['approved', 'converted'].includes(data.status)) {
          setPageState('already_actioned');
        } else if (data.status === 'denied') {
          setPageState('already_actioned');
        } else if (data.status === 'void') {
          setPageState('expired');
        } else if (data.expiresAt && new Date(data.expiresAt) < new Date()) {
          setPageState('expired');
        } else {
          setPageState('ready');
        }
      })
      .catch((err) => {
        setPageState('error');
        setErrorMessage(err.message || t('Unable to load quote.'));
      });
  }, [t, token]);

  const handleApprove = async () => {
    if (!token) return;
    setActionLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/quote-approval/${token}/approve`, { method: 'POST' });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ message: t('Failed to approve quote') }));
        throw new Error(err.message);
      }
      setPageState('approved');
    } catch (err: any) {
      setErrorMessage(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeny = async () => {
    if (!token) return;
    setActionLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/quote-approval/${token}/deny`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: denialReason || undefined }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ message: t('Failed to decline quote') }));
        throw new Error(err.message);
      }
      setPageState('denied');
    } catch (err: any) {
      setErrorMessage(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const getCustomerName = () => {
    if (!quote?.customer) return '';
    if (quote.customer.companyName) return quote.customer.companyName;
    return `${quote.customer.firstName || ''} ${quote.customer.lastName || ''}`.trim();
  };

  const primaryColor = quote?.account?.primaryColor || '#016ec3';

  // Loading
  if (pageState === 'loading') {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#f5f7fa', p: 3 }}>
        <CircularProgress size={48} />
      </Box>
    );
  }

  // Error
  if (pageState === 'error') {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#f5f7fa', p: 3 }}>
        <Paper sx={{ maxWidth: 480, width: '100%', p: 4, textAlign: 'center' }}>
          <ErrorIcon sx={{ fontSize: 56, color: 'error.main', mb: 2 }} />
          <Typography variant="h5" gutterBottom>{t('Unable to Load Quote')}</Typography>
          <Typography color="text.secondary">{errorMessage || t('This quote link may be invalid or expired.')}</Typography>
        </Paper>
      </Box>
    );
  }

  // Expired
  if (pageState === 'expired') {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#f5f7fa', p: 3 }}>
        <Paper sx={{ maxWidth: 480, width: '100%', p: 4, textAlign: 'center' }}>
          <ErrorIcon sx={{ fontSize: 56, color: 'warning.main', mb: 2 }} />
          <Typography variant="h5" gutterBottom>{t('Quote Expired')}</Typography>
          <Typography color="text.secondary">
            {t('This quote is no longer available. Please contact {{provider}} for an updated quote.', { provider: quote?.account?.businessName || t('your service provider') })}
          </Typography>
          {quote?.account?.businessPhone && (
            <Typography sx={{ mt: 2 }} color="text.secondary">
              {t('Phone')}: {quote.account.businessPhone}
            </Typography>
          )}
        </Paper>
      </Box>
    );
  }

  // Approved success
  if (pageState === 'approved') {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#f5f7fa', p: 3 }}>
        <Paper sx={{ maxWidth: 480, width: '100%', p: 4, textAlign: 'center' }}>
          <ApproveIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
          <Typography variant="h5" gutterBottom>{t('Quote Approved!')}</Typography>
          <Typography color="text.secondary" sx={{ mb: 2 }}>
            {t('Thank you for approving this quote. {{businessName}} has been notified and will be in touch regarding next steps.', { businessName: quote?.account?.businessName })}
          </Typography>
          {quote && quote.depositAmount > 0 && (
            <Alert severity="info" sx={{ mt: 2, textAlign: 'left' }}>
              {t('A deposit of {{amount}} is required. Your service provider will follow up with payment details.', { amount: formatCurrency(quote.depositAmount, quote) })}
            </Alert>
          )}
        </Paper>
      </Box>
    );
  }

  // Denied success
  if (pageState === 'denied') {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#f5f7fa', p: 3 }}>
        <Paper sx={{ maxWidth: 480, width: '100%', p: 4, textAlign: 'center' }}>
          <DenyIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h5" gutterBottom>{t('Quote Declined')}</Typography>
          <Typography color="text.secondary">
            {t("You've declined this quote. If you change your mind or have questions, please contact {{provider}}.", { provider: quote?.account?.businessName || t('your service provider') })}
          </Typography>
        </Paper>
      </Box>
    );
  }

  // Already actioned
  if (pageState === 'already_actioned' && quote) {
    const isApproved = ['approved', 'converted'].includes(quote.status);
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#f5f7fa', p: 3 }}>
        <Paper sx={{ maxWidth: 480, width: '100%', p: 4, textAlign: 'center' }}>
          {isApproved ? (
            <ApproveIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
          ) : (
            <DenyIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          )}
          <Typography variant="h5" gutterBottom>
            {t('Quote Already {{status}}', { status: isApproved ? t('Approved') : t('Declined') })}
          </Typography>
          <Typography color="text.secondary">
            {t('This quote has already been {{status}}. No further action is needed.', { status: isApproved ? t('approved') : t('declined') })}
          </Typography>
        </Paper>
      </Box>
    );
  }

  // Ready - show quote details with approve/deny
  if (!quote) return null;

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f5f7fa', py: 4, px: 2 }}>
      <Box sx={{ maxWidth: 680, mx: 'auto' }}>
        {/* Business Header */}
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          {quote.account.logoUrl && (
            <Box
              component="img"
              src={quote.account.logoUrl}
              alt={quote.account.businessName}
              sx={{ height: 96, maxWidth: 320, objectFit: 'contain', mb: 1 }}
            />
          )}
          <Typography variant="h6" fontWeight="bold">
            {quote.account.businessName}
          </Typography>
        </Box>

        <Paper sx={{ p: { xs: 3, sm: 4 } }}>
          {/* Quote Header */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
            <Box>
              <Typography variant="h5" fontWeight="bold">{quote.quoteNumber}</Typography>
              {quote.title && (
                <Typography variant="body1" color="text.secondary" sx={{ mt: 0.5 }}>{quote.title}</Typography>
              )}
            </Box>
            <Chip label={t('Pending Approval')} color="info" />
          </Box>

          {/* Customer / Property */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="body2" color="text.secondary">{t('Prepared for')}</Typography>
            <Typography fontWeight="medium">{getCustomerName()}</Typography>
            {quote.property && (
              <Typography variant="body2" color="text.secondary">
                {quote.property.addressLine1}, {quote.property.city}, {quote.property.state}
              </Typography>
            )}
          </Box>

          <Divider sx={{ mb: 3 }} />

          {/* Line Items */}
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('Description')}</TableCell>
                  <TableCell align="center">{t('Qty')}</TableCell>
                  <TableCell align="right">{t('Price')}</TableCell>
                  <TableCell align="right">{t('Amount')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {quote.lineItems.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell>{item.description}</TableCell>
                    <TableCell align="center">{item.quantity}</TableCell>
                    <TableCell align="right">{formatCurrency(item.unitPrice, quote)}</TableCell>
                    <TableCell align="right">{formatCurrency(item.amount, quote)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Totals */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
            <Box sx={{ minWidth: 240 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                <Typography variant="body2">{t('Subtotal')}</Typography>
                <Typography variant="body2">{formatCurrency(quote.subtotal, quote)}</Typography>
              </Box>
              {quote.taxAmount > 0 && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                  <Typography variant="body2">{t('Tax')}</Typography>
                  <Typography variant="body2">{formatCurrency(quote.taxAmount, quote)}</Typography>
                </Box>
              )}
              <Divider sx={{ my: 1 }} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                <Typography fontWeight="bold" variant="h6">{t('Total')}</Typography>
                <Typography fontWeight="bold" variant="h6">{formatCurrency(quote.total, quote)}</Typography>
              </Box>
              {quote.depositAmount > 0 && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5, mt: 1 }}>
                  <Typography variant="body2" sx={{ color: primaryColor }}>
                    {t('Deposit Required')} ({quote.depositPercent}%)
                  </Typography>
                  <Typography variant="body2" fontWeight="bold" sx={{ color: primaryColor }}>
                    {formatCurrency(quote.depositAmount, quote)}
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>

          {/* Notes */}
          {quote.notes && (
            <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>{t('Notes')}</Typography>
              <Typography variant="body2">{quote.notes}</Typography>
            </Box>
          )}

          {/* Expiration */}
          {quote.expiresAt && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2, textAlign: 'center' }}>
              {t('This quote is valid until {{date}}', { date: formatDate(quote.expiresAt, quote.account.settings?.locale || quote.account.locale) })}
            </Typography>
          )}

          <Divider sx={{ my: 3 }} />

          {/* Error message */}
          {errorMessage && (
            <Alert severity="error" sx={{ mb: 2 }}>{errorMessage}</Alert>
          )}

          {/* Actions */}
          {showDenyForm ? (
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                {t('Reason for declining (optional)')}
              </Typography>
              <TextField
                multiline
                rows={2}
                fullWidth
                placeholder={t("Let us know if there's anything we can adjust...")}
                value={denialReason}
                onChange={(e) => setDenialReason(e.target.value)}
                size="small"
                sx={{ mb: 2 }}
              />
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  variant="outlined"
                  onClick={() => setShowDenyForm(false)}
                  disabled={actionLoading}
                  fullWidth
                >
                  {t('Back')}
                </Button>
                <Button
                  variant="contained"
                  color="error"
                  onClick={handleDeny}
                  disabled={actionLoading}
                  startIcon={actionLoading ? <CircularProgress size={16} /> : undefined}
                  fullWidth
                >
                  {t('Confirm Decline')}
                </Button>
              </Box>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                variant="outlined"
                color="inherit"
                onClick={() => setShowDenyForm(true)}
                disabled={actionLoading}
                startIcon={<DenyIcon />}
                fullWidth
                sx={{ color: 'text.secondary' }}
              >
                {t('Decline')}
              </Button>
              <Button
                variant="contained"
                onClick={handleApprove}
                disabled={actionLoading}
                startIcon={actionLoading ? <CircularProgress size={16} /> : <ApproveIcon />}
                fullWidth
                sx={{
                  bgcolor: primaryColor,
                  '&:hover': { bgcolor: primaryColor, opacity: 0.9 },
                }}
              >
                {t('Approve Quote')}
              </Button>
            </Box>
          )}

          {/* Footer */}
          {quote.footerNotes && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 3, display: 'block', textAlign: 'center' }}>
              {quote.footerNotes}
            </Typography>
          )}
        </Paper>
      </Box>
    </Box>
  );
};
